import React from 'react';

import { Messages } from '../../../codegen/Localize';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { getCommonDatasourceInfo } from '../CommonDatasourceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { DatasourceTooltip } from '../Tooltips/DatasourceTooltip';
import { TooltipAnchor } from '../Tooltips/TooltipAnchor';
import { CardViewStyles } from './CardViewStyles';
import { ConnectedDetailedDatasourceThumbnail } from './ConnectedDetailedDatasourceThumbnail';
import { ContentCard, getContentUrlForItem, ContentCardProps } from './ContentCard';
import { DetailsTooltipStyles } from './DetailsTooltipStyles';
import { Thumbnail } from './Thumbnail';
import { doesTooltipNotHaveOwnerToDisplay } from '../Tooltips/Elements/Tooltip';
import { DataSources } from '../../../content/resources/Datasources';

export const DatasourceDefaultImageUrl = imgPath('data-source-icon-loading.svg');

export class DatasourceCard extends ContentCard {

  private isUnifiedDatasource(): boolean {
    return DataSources.isUnifiedDatasource(this.props.content);
  }

  private getDatasourceId(): string{
    if(this.isUnifiedDatasource()){
      return (this.props.content as IDataSourceInfo).idForFavoriting;
    }
    else {
      return this.props.content.id;
    }
  }

  public render() {
    const contentUrl = getDatasourceContentUrl(this.props.urls, this.props.content, this.props.inWorkbookDatasourcesSubspace);

    let thumbnail: JSX.Element = null;
    if (hasExtraDatasourceDetail(this.props.content)) {
      thumbnail = detailedDatasourceThumbnail(contentUrl, this.props);
    } else {
      thumbnail = <Thumbnail navigateUrl={contentUrl}
                             defaultImageUrl={DatasourceDefaultImageUrl}
                             allowDragAndDrop={this.props.allowDragAndDrop}
                             hoveredOrSelected={this.props.hoveredOrSelected} />;
    }

    const commonDatasource = getCommonDatasourceInfo(this.props.content);

    const favoritableItemInfo: IFavoritableItemInfo = {
      ...this.props.content,
      contentType: 'datasource',
      idForFavoriting: this.getDatasourceId(),
    };

    const disableAutoFocus = DataSources.isEmbedded(commonDatasource) || doesTooltipNotHaveOwnerToDisplay(this.props.content);

    return (
      <div className={CardViewStyles.Card}>
        <TooltipAnchor tooltipContent={this.renderTooltipContent}
                       width={DetailsTooltipStyles.Width}
                       onShowTooltip={this.handleShowTooltip}
                       disableAutoFocus={disableAutoFocus}
                       title={Messages.datasourceDetails()}
                       disableTooltip={this.props.disableTooltip}>
            { thumbnail }
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow}>
          <span data-tb-test-id="datasource-name" className={CardViewStyles.ActionRowName} title={this.props.content.name}>
            { this.props.content.name }
          </span>
          {!DataSources.isEmbedded(commonDatasource) && this.showFavoritesStar() &&
            <span>
              <FavoriteToggle isTabbable={true}
                              isFavorite={this.props.content.favorite}
                              onAddFavorite={() => this.props.addContentFavorite(favoritableItemInfo)}
                              onRemoveFavorite={() => this.props.removeContentFavorite(favoritableItemInfo)}/>
            </span>
          }
          { this.renderActionsMenu() }
        </div>
        <div className={CardViewStyles.CardDescription}>
          {this.getDescription()}
        </div>
      </div>
    );
  }

  private renderTooltipContent = () => {
    return <DatasourceTooltip content={this.props.content}
                              urls={this.props.urls}
                              inProjectSpace={this.props.inProjectSpace}
                              inPersonalSpace={this.props.inPersonalSpace}/>;
  }
}

export function getDatasourceContentUrl(
    urls: SpacesSiteUrls,
    content: IContentInfo,
    inWorkbookDatasourcesSubspace: boolean
): string {
  const commonDatasource = getCommonDatasourceInfo(content);

  const workbook = DataSources.isEmbedded(commonDatasource) ? commonDatasource.workbook : undefined;
  if (workbook) {
    // If we're already in a workbook place, the card href would link us to where we already are,
    // so don't make it a link.
    if (inWorkbookDatasourcesSubspace) {
      return undefined;
    }

    return urls.workbookDatasourcesUrl(workbook.id);
  }

  if (DataSources.isUnifiedDatasource(content)) {
    return urls.datasourceUrl(content.id);
  }

  return getContentUrlForItem(content, urls);
}

function showExtractEncryptionStatus(): boolean {
    const sessionManagementService = getSessionManagementServiceInstance();
    return sessionManagementService.isExtractEncryptionEnabledOmniSwitch();
}

function isUnifiedDatasource(content: IContentInfo): boolean {
  return DataSources.isUnifiedDatasource(content);
}

// following functions are exported so they can be used as part of other mixed content cards (such as SharedWithMeCard)

export function hasExtraDatasourceDetail(content: IContentInfo): boolean {
  return isUnifiedDatasource(content) ||
    (content.contentType.toLowerCase() === 'datasource' && content.contentDetail);
}

export function detailedDatasourceThumbnail(contentUrl: string, props: ContentCardProps, ariaLabel?: string): JSX.Element {
  let thumbnailModel = null;
  if (isUnifiedDatasource(props.content)) {
    thumbnailModel = getCommonDatasourceInfo(props.content as IDataSourceInfo);
  } else if (props.content.contentDetail) {
    thumbnailModel = getCommonDatasourceInfo(props.content as IContentInfo);
  }

  return (
    <ConnectedDetailedDatasourceThumbnail
      urls={props.urls}
      contentUrl={contentUrl}
      hoveredOrSelected={props.hoveredOrSelected}
      datasource={thumbnailModel}
      allowDragAndDrop={props.allowDragAndDrop}
      showExtractEncryptionStatus={showExtractEncryptionStatus()}
      ariaLabel={ariaLabel}
    />
  );
}
